import React, { useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import AuthLayout from '../../components/auth/AuthLayout';
import { auth, db } from '../../../firebaseConfig.js';
import { uploadUserImage } from '../../lib/uploadProfileImage';
import { logStorageError } from '../../lib/firebase/logStorageError';

const Step1 = ({ onNavigate }) => {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [prefillLoaded, setPrefillLoaded] = useState(false);
  const [existingCreatedAt, setExistingCreatedAt] = useState(null);
  const [existingProfileExists, setExistingProfileExists] = useState(null);

  const [displayName, setDisplayName] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');
  const [existingPhotoUrl, setExistingPhotoUrl] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSavingCrop, setIsSavingCrop] = useState(false);

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreviewUrl('');
      return;
    }

    const objectUrl = URL.createObjectURL(photoFile);
    setPhotoPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [photoFile]);

  const handleCropComplete = (_, croppedPixels) => {
    if (croppedPixels) {
      setCroppedAreaPixels(croppedPixels);
    }
  };

  const getCroppedImg = async (imageSrc, croppedPixels) => {
    const image = new Image();
    image.src = imageSrc;
    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2d context');

    canvas.width = croppedPixels.width;
    canvas.height = croppedPixels.height;

    ctx.drawImage(
      image,
      croppedPixels.x,
      croppedPixels.y,
      croppedPixels.width,
      croppedPixels.height,
      0,
      0,
      croppedPixels.width,
      croppedPixels.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setSubmitError('Please select an image file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setSubmitError('Image size must be less than 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result);
      setIsCropModalOpen(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    };
    reader.readAsDataURL(file);

    event.target.value = '';
  };

  const handleSaveCroppedPhoto = async () => {
    if (!selectedImage || !croppedAreaPixels) return;

    try {
      setIsSavingCrop(true);
      const blob = await getCroppedImg(selectedImage, croppedAreaPixels);
      const croppedFile = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
      setPhotoFile(croppedFile);
      setSelectedImage(null);
      setIsCropModalOpen(false);
    } catch (error) {
      console.error('[onboarding] Failed to crop photo', error);
      setSubmitError('We could not crop your photo. Please try again.');
    } finally {
      setIsSavingCrop(false);
    }
  };

  // Load profile from Firestore whenever we have a user (e.g. on mount and when navigating back to this step)
  useEffect(() => {
    if (!user || prefillLoaded) return;

    let isMounted = true;
    const loadProfile = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (!isMounted) return;

        if (snap.exists()) {
          const data = snap.data() || {};
          // Replace form state from Firestore so back-navigation always shows saved data
          setDisplayName(data.displayName ?? '');
          setExistingPhotoUrl(data.photoURL || '');
          setExistingCreatedAt(data.createdAt || null);
          setExistingProfileExists(true);
        } else {
          setDisplayName('');
          setExistingPhotoUrl('');
          setExistingProfileExists(false);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[onboarding] Failed to load profile data', error);
        }
      } finally {
        if (isMounted) {
          setPrefillLoaded(true);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [user, prefillLoaded]);

  const getFieldErrors = () => {
    const errors = {};
    if (!displayName.trim()) {
      errors.displayName = 'Display name is required.';
    }
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitAttempted(true);
    setSubmitError('');

    const errors = getFieldErrors();
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (!user) {
      onNavigate('login');
      return;
    }

    setLoading(true);

    try {
      const normalizedDisplayName = displayName.trim();
      const profileRef = doc(db, 'users', user.uid);

      let shouldSetCreatedAt = true;
      if (existingProfileExists === true) {
        shouldSetCreatedAt = !existingCreatedAt;
      } else if (existingProfileExists === null) {
        const snap = await getDoc(profileRef);
        if (snap.exists()) {
          const data = snap.data() || {};
          setExistingProfileExists(true);
          setExistingCreatedAt(data.createdAt || null);
          shouldSetCreatedAt = !data.createdAt;
        } else {
          setExistingProfileExists(false);
        }
      }

      const payload = {
        displayName: normalizedDisplayName,
        onboarding: {
          step: 1,
          status: 'in_progress',
          updatedAt: serverTimestamp(),
        },
      };

      if (shouldSetCreatedAt) {
        payload.createdAt = serverTimestamp();
      }

      // Create users doc FIRST so Storage rules (which may read users/{uid}) see it and don't return 412
      await setDoc(profileRef, payload, { merge: true });

      let photoUrl = null;
      if (photoFile) {
        try {
          photoUrl = await uploadUserImage(photoFile, user.uid, 'profile');
          await setDoc(profileRef, { photoURL: photoUrl }, { merge: true });
        } catch (uploadError) {
          const extension = photoFile.name?.split('.').pop()?.toLowerCase() || 'jpg';
          const uploadPath = `uploads/${user.uid}/profile.${extension}`;
          const uiError = logStorageError(uploadError, {
            uid: user.uid,
            uploadPath,
            projectId: import.meta.env?.VITE_FIREBASE_PROJECT_ID,
            storageBucketFromConfig: import.meta.env?.VITE_FIREBASE_STORAGE_BUCKET,
            fileType: photoFile.type ?? undefined,
            fileSizeBytes: photoFile.size,
            flow: 'onboarding-step1',
          });
          setSubmitError(
            `Your profile was saved, but the photo could not be uploaded. ${uiError} You can add a photo later from your profile.`
          );
        }
      }

      // Also save displayName and photoURL to influencers collection so public profile can display them
      // This ensures data from onboarding appears on both edit and public profiles
      try {
        const influencerRef = doc(db, 'influencers', user.uid);
        const influencerExisting = await getDoc(influencerRef);
        const existingInfluencer = influencerExisting.exists() ? influencerExisting.data() : {};
        const existingIdentity = existingInfluencer?.identity || {};

        const influencerData = {
          userId: user.uid,
          updatedAt: serverTimestamp(),
        };

        // Sync displayName to influencers collection
        if (normalizedDisplayName) {
          influencerData.displayName = normalizedDisplayName;
          influencerData.identity = {
            ...existingIdentity,
            displayName: normalizedDisplayName,
          };
        } else {
          // Preserve existing identity if no new displayName
          influencerData.identity = existingIdentity;
        }

        // Sync photoURL to influencers collection
        if (photoUrl) {
          influencerData.photoURL = photoUrl;
          influencerData.profileImageUrl = photoUrl;
          influencerData.avatarUrl = photoUrl;
          influencerData.identity = {
            ...influencerData.identity,
            photoURL: photoUrl,
            profileImageUrl: photoUrl,
            avatarUrl: photoUrl,
          };
        }

        // Preserve createdAt if exists
        if (existingInfluencer?.createdAt) {
          influencerData.createdAt = existingInfluencer.createdAt;
        } else {
          influencerData.createdAt = serverTimestamp();
        }

        await setDoc(influencerRef, influencerData, { merge: true });
      } catch (influencerError) {
        // Log but don't block onboarding if influencers write fails
        console.error('[onboarding] Failed to save to influencers collection:', influencerError);
      }

      onNavigate('onboarding/step-2');
    } catch (error) {
      console.error('[onboarding] Failed to save step 1', error);
      setSubmitError('We could not save your profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fieldErrors = getFieldErrors();

  if (!authChecked) {
    return (
      <AuthLayout
        title="Let’s set up your profile"
        subtitle="Step 1 of 3 — Identity"
        cardClassName="px-8 py-6"
      >
        <div className="text-center text-sm text-slate-500">Loading...</div>
      </AuthLayout>
    );
  }

  // While profile is loading from Firestore (e.g. after navigating back), show loading so we don't flash empty form
  if (user && !prefillLoaded) {
    return (
      <AuthLayout
        title="Let's set up your profile"
        subtitle="Step 1 of 3 — Identity"
        cardClassName="px-8 py-6"
      >
        <div className="text-center text-sm text-slate-500">Loading your profile...</div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Let’s set up your profile"
      subtitle="Step 1 of 3 — Identity"
      cardClassName="px-8 py-6"
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="displayName" className="text-sm font-medium text-slate-700">
            Display Name
          </label>
          <input
            id="displayName"
            name="displayName"
            type="text"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            placeholder="Your public name"
            autoComplete="name"
          />
          <div className="mt-1 min-h-[18px] text-sm text-red-500">
            {submitAttempted && fieldErrors.displayName ? fieldErrors.displayName : ''}
          </div>
        </div>

        <div>
          <label htmlFor="photoUpload" className="text-sm font-medium text-slate-700">
            Profile Photo
          </label>
          <input
            id="photoUpload"
            name="photoUpload"
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="mt-1 block w-full text-sm text-slate-600 file:mr-4 file:rounded-md file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
          />
          <p className="mt-2 text-sm text-slate-500">
            Max size 5MB.
            {photoFile && (
              <span className="ml-2 text-slate-700">Selected: {photoFile.name}</span>
            )}
          </p>

          {(photoPreviewUrl || existingPhotoUrl) && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-4 text-center">
              <div className="mx-auto h-32 w-32 rounded-full border-4 border-[#1B9DA2] bg-white p-0.5">
                <img
                  src={photoPreviewUrl || existingPhotoUrl}
                  alt="Profile preview"
                  className="h-full w-full rounded-full object-cover"
                />
              </div>
              <div className="mt-3 text-sm text-slate-600">
                {photoPreviewUrl ? 'New photo selected' : 'Current photo on file'}
              </div>
            </div>
          )}
        </div>

        {submitError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {submitError}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={loading}
          className="h-12 w-full rounded-lg bg-[#1B9DA2] text-sm font-medium text-white transition hover:bg-[#168589] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Continue'}
        </button>
      </form>
      {isCropModalOpen && selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl mx-4">
            <h2 className="text-base font-semibold text-slate-900">
              Adjust your profile photo
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Drag to reposition and use the slider to zoom.
            </p>

            <div className="mt-4 relative h-64 w-full bg-slate-100 rounded-xl overflow-hidden">
              <Cropper
                image={selectedImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={handleCropComplete}
              />
            </div>

            <div className="mt-4 flex items-center gap-3">
              <span className="text-xs text-slate-500">Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-xs text-slate-500 w-8 text-right">{zoom.toFixed(1)}x</span>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  if (isSavingCrop) return;
                  setIsCropModalOpen(false);
                  setSelectedImage(null);
                  setCroppedAreaPixels(null);
                }}
                className="text-sm font-medium text-slate-500 hover:text-slate-700 px-4 py-2 rounded-lg transition-colors"
                disabled={isSavingCrop}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveCroppedPhoto}
                className="rounded-lg bg-[#1B9DA2] px-4 py-2 text-sm font-semibold text-white hover:bg-[#168589] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                disabled={isSavingCrop}
              >
                {isSavingCrop ? 'Saving...' : 'Save photo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default Step1;
